import {useParams} from "react-router-dom";
import {useCallback, useEffect, useMemo, useState} from "react";
import {UpdateOrderComponent} from "../../component/UpdateOrderComponent";
import {Layout} from "../../component/layout/Layout";
import {get, handleApiError, handleExpiredEntryLinkError, patch, post, put} from "../../js/api";
import {OrderInformation} from "../../component/OrderInformation";
import {AddressInput, empty as emptyAddress} from "../../component/form/AddressInput";
import {InputCheckbox} from "../../component/form/InputCheckbox";
import {Button} from "../../component/form/Button";
import {WorkflowProgress} from "../../component/layout/WorkflowProgress";
import {orderCorrespondenceType, orderType} from "../../js/constants";
import {OrderCorrespondence, prepareOrderCorrespondenceMessage} from "../../component/OrderCorrespondence";
import {showError, showMessage} from "../../js/main";
import {RefreshSignal} from "../../js/util";
import {useTanPrefill} from "../../js/useTanPrefill";
import {AccordionSection} from "../../component/AccordionSection";
import {getKeyIdFromSourceIdentifier} from "../../js/orderItemSource";

const refresh = new RefreshSignal();
const adminCorrespondenceTypes = [
    orderCorrespondenceType.information,
    orderCorrespondenceType.question,
    orderCorrespondenceType.keycard
];

export const UpdatePublicOrder = ({}) => {

    const {token} = useParams();
    const initialTan = useTanPrefill();

    const [useBillingAddressForShipping, setUseBillingAddressForShipping] = useState(true);
    const [billingAddress, setBillingAddress] = useState(null);
    const [shippingAddress, setShippingAddress] = useState(emptyAddress);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [itemAmountsDirty, setItemAmountsDirty] = useState(false);

    const [reply, setReply] = useState(null);
    const [showReply, setShowReply] = useState(false);
    const [communicationOpen, setCommunicationOpen] = useState(false);

    const [submitting, setSubmitting] = useState(false);

    const [currentStep, setCurrentStep] = useState();
    const workflowSteps = useMemo(() => ["Kontaktdaten angeben", "Bestätigung"], []);

    const openCommunicationForAdminMessage = useCallback((messages) => {
        const lastMessage = messages?.[messages.length - 1];
        if (lastMessage && adminCorrespondenceTypes.includes(lastMessage.type)) {
            setCommunicationOpen(true);
        }
    }, []);

    useEffect(() => {
        try{
            JSON.parse(new TextDecoder().decode(
                Uint8Array.from(atob(token), c => c.charCodeAt(0))
            ));
        }catch (err){
            showError("Der Link ist ungültig. Klicken Sie bitte entweder auf den Link aus der E-Mail. Falls Sie ihn kopieren und einfügen müssen gehen Sie bitte sicher, dass er vollständig kopiert wird.")
            return;
        }
        get(`/public/orders/${token}`, false).then((order) => {
            if (order.type == orderType.request) {
                window.location = `/guest-request/${token}`;
                return;
            }
            setSelectedOrder(order);
            setItemAmountsDirty(false);
            const withEmail = {...billingAddress, email: order.email}; //prefill with info we have
            setBillingAddress(withEmail);
        }).catch(e => {
            if (!handleExpiredEntryLinkError(e)) {
                handleApiError(e);
            }
        })
    }, []);

    useEffect(() => {
        if (!selectedOrder) {
            return;
        }
        if (selectedOrder.billingAddress) {
            setCurrentStep(workflowSteps[1]);
        } else {
            setCurrentStep(workflowSteps[0]);
        }
    }, [selectedOrder])

    const updateItemAmount = useCallback((row, changes) => {
        setSelectedOrder(currentOrder => {
            if (!currentOrder) {
                return currentOrder;
            }

            const itemChanges = typeof changes === "number" ? {amount: changes} : changes;
            const updatedItems = currentOrder.items.map(item => {
                const sameItem = row.id !== undefined && row.id !== null
                    ? item.id === row.id
                    : item === row;

                return sameItem ? {...item, ...itemChanges} : item;
            });

            return {...currentOrder, items: updatedItems};
        });
        setItemAmountsDirty(true);
    }, []);

    const updateItemAmounts = async () => {
        if (!itemAmountsDirty || selectedOrder.username) {
            return selectedOrder;
        }

        const items = await Promise.all(selectedOrder.items
            .filter(item => getKeyIdFromSourceIdentifier(item.sourceIdentifier))
            .map(item => post(`/public/keys/${getKeyIdFromSourceIdentifier(item.sourceIdentifier)}/discountOrderItem`, {
                orderItemId: item.id,
                amount: item.amount
            }, false)));

        if (items.length === 0) {
            return selectedOrder;
        }

        const updates = {items};

        const updatedOrder = await patch(`/public/orders/${token}/item-amounts`, updates, false);
        return updatedOrder || selectedOrder;
    }

    const saveAddresses = async () => {
        setSubmitting(true);

        try {
            const orderWithUpdatedAmounts = await updateItemAmounts();

            const updates = {
                ...orderWithUpdatedAmounts,
                billingAddress: billingAddress,
                shippingAddress: useBillingAddressForShipping ? null : shippingAddress
            };

            const updatedOrder = await put(`/public/orders/${token}/updateAddresses`, updates, false);
            setSelectedOrder(updatedOrder);
            setItemAmountsDirty(false);
        } catch (e) {
            if (!handleExpiredEntryLinkError(e)) {
                handleApiError(e);
            }
        } finally {
            setSubmitting(false);
        }
    }

    const sendReply = async () => {
        setSubmitting(true);
        try {
            const message = await prepareOrderCorrespondenceMessage(reply, true);
            await post(`/public/orders/${token}/correspondence`, message, false);
            showMessage("Die Nachricht wurde gesendet");
            refresh.changed();
            if (selectedOrder.state == 'AWAIT_USER') {
                setSelectedOrder(await get(`/public/orders/${token}`, false));
            }
        } catch (e) {
            if (!handleExpiredEntryLinkError(e)) {
                showError("Die Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.");
            }
        } finally {
            setSubmitting(false);
        }
    }


    return (
        <>
            <Layout username={null}>
                <WorkflowProgress steps={workflowSteps} currentStep={currentStep}></WorkflowProgress>
                {selectedOrder && <>
                    {workflowSteps.indexOf(currentStep) == 0 && billingAddress && <>
                            <h1>Offenes Bestellungsangebot {selectedOrder.id}</h1>
                            <div className="customer-accordion">
                                <AccordionSection title="Bestellungsangebot" defaultOpen>
                                    <p>Wir freuen uns, Ihnen das folgende Angebot machen zu können. Wenn Sie mit dem Angebot
                                        einverstanden sind, benötigen wir nur noch Ihre Kontaktdaten von Ihnen.</p>
                                    <OrderInformation order={selectedOrder} hideHeader onItemAmountChange={updateItemAmount}/>
                                </AccordionSection>
                                <AccordionSection title="Kommunikation"
                                                  open={communicationOpen}
                                                  onOpenChange={setCommunicationOpen}>
                                    <form onSubmit={(e) => {
                                        e.preventDefault();
                                        sendReply();
                                    }}>
                                        <OrderCorrespondence isPublic={true} identifier={token}
                                                             onCreateReply={showReply ? setReply : null}
                                                             onMessagesLoaded={openCommunicationForAdminMessage}
                                                             adminView={false} refresh={refresh}/>
                                        {reply && <div className={"safe-actions mt-1"}>
                                            <Button key="submit" theme="blue mb-1"
                                                    width={230}
                                                    submitting={submitting}>Antwort
                                                senden</Button>
                                        </div>}
                                        {!showReply && <div className={"safe-actions mt-1"}>
                                            <Button key="submit"
                                                    theme="blue mb-1"
                                                    width={230} onClick={(e) => {
                                                e.preventDefault();
                                                setShowReply(true);
                                            }}
                                                    submitting={submitting}>Beantworten / Rückfrage
                                            </Button>
                                        </div>}
                                    </form>
                                </AccordionSection>
                                <AccordionSection title="Kontaktdaten" collapsible={false}>
                                    <form onSubmit={(e) => {
                                        e.preventDefault();
                                        saveAddresses();
                                    }}>
                                        <h2 className="mt-0 mb-05">Rechnungsadresse auswählen</h2>
                                        <AddressInput initial={billingAddress}
                                                      setter={setBillingAddress}
                                                      type={useBillingAddressForShipping ? 'BOTH' : 'BILLING'}></AddressInput>
                                        <div className="default mb-1">
                                            <InputCheckbox label="Lieferadresse entspricht Rechnungsadresse"
                                                           big
                                                           value={useBillingAddressForShipping}
                                                           setter={(newValue) => setUseBillingAddressForShipping(newValue)}/>
                                        </div>
                                        {!useBillingAddressForShipping &&
                                            <AddressInput initial={shippingAddress}
                                                          setter={setShippingAddress}
                                                          type="SHIPPING"
                                                          optionals/>}

                                        <div className={"safe-actions mt-1"}>
                                            <Button key="submit" theme="primary-button mb-1" width={230}
                                                    submitting={submitting}>weiter</Button>
                                        </div>
                                    </form>
                                </AccordionSection>
                            </div>
                    </>}

                    {workflowSteps.indexOf(currentStep) == 1 && <>
                        <div className="customer-accordion">
                            <AccordionSection title="Bestellung bestätigen" collapsible={false}>
                                <UpdateOrderComponent identifier={token} token={true} disableCancel={true} initialTan={initialTan}></UpdateOrderComponent>
                            </AccordionSection>
                            <AccordionSection title="Kommunikation"
                                              open={communicationOpen}
                                              onOpenChange={setCommunicationOpen}>
                                <form onSubmit={(e) => {
                                    e.preventDefault();
                                    sendReply();
                                }}>
                                    <OrderCorrespondence isPublic={true} identifier={token} onCreateReply={setReply}
                                                         onMessagesLoaded={openCommunicationForAdminMessage}
                                                         adminView={false} refresh={refresh}/>
                                    {reply && <div className={"safe-actions mt-1"}>
                                        <Button key="submit" theme="blue mb-1"
                                                width={230}
                                                submitting={submitting}>Antwort
                                            senden</Button>
                                    </div>}
                                </form>
                            </AccordionSection>
                        </div>
                    </>}

                </>}
            </Layout>
        </>
    );

};
